import { useState, useEffect, useMemo } from 'react';
import { t } from '../../translations/i18n';
import { Feature, FeatureCollection } from 'geojson';
import { store } from '../../store/store';
import { useSelector } from 'react-redux';
import { debounce } from 'lodash-es';
import TextField from '@mui/material/TextField';
import { MmgOperationContainer } from '../../shared/operations/operation-container';
import { OPERATION_DESCRIPTION_STYLES, OPERATION_INPUT_STYLES } from './operation-styles';
import { OPERATION_TOOL_IDS } from './operation-constants';
import { EMapToolActionType } from '../../store/actions/MapToolActionType';
import MikeVisualizer from '../../MikeVisualizer';
import { IGlobalState } from '../../store/reducers';
import GeometryFactory from 'jsts/org/locationtech/jts/geom/GeometryFactory';
import GeoJSONReader from 'jsts/org/locationtech/jts/io/GeoJSONReader';
import GeoJSONWriter from 'jsts/org/locationtech/jts/io/GeoJSONWriter';
import Polygon from 'jsts/org/locationtech/jts/geom/Polygon';
import LinearRing from 'jsts/org/locationtech/jts/geom/LinearRing'
import { EDIT_MODE, EDIT_MODE_MODIFIED } from '../../store/reducers/EditReducer';
import { MmgInputUnitAdornment } from '../parameters/input-unit-Adornment';

const { getCurrentlyDrawnGeojson, setDrawVectorLayerGeojson } = MikeVisualizer;

const emptyFeatureCollection: FeatureCollection<any, any> = {
  type: 'FeatureCollection',
  features: [],
};

type DissolveOperationProps = {
  setOperationDone?: (fc: FeatureCollection<any, any>, mixedEditModeSet: boolean) => void;
  disabled?: boolean;
};

/**
 * @name MmgConnectedDissolveOperation
 * @summary Gets currently drawn geojson and dissolves.
 *
 * @param props
 */
export function MmgConnectedDissolveOperation(props: DissolveOperationProps) {
  const { setOperationDone, disabled } = props;
  const { polygonDrawingEnabled, polylineDrawingEnabled, pointDrawingEnabled } = useSelector(
    (state: IGlobalState) => state.MapToolReducer,
  );
  const activeOperationToolId = useSelector(
    (state: IGlobalState) => state.GeometryClientOperationReducer.activeOperationToolId,
  );

  const workspace = useSelector((state: IGlobalState) => state.WorkspaceReducer.workspace);
    
  const {isLatLong, unit} = useMemo(
    () => {
      if (workspace){
        return {isLatLong: workspace.isLatLon, unit: workspace.unit}
      }      
    },
    [workspace],
  ); 
    
  const isOpen = activeOperationToolId === OPERATION_TOOL_IDS.DISSOLVE;
  const [minGapArea, setMinGapArea] = useState(('' as unknown) as number);

  const [initialFeatureCollection, setInitialFeatureCollection] = useState({
    ...emptyFeatureCollection,
  });

  const handleInputChange = (event) => {
    const dissolveGeometry = debounce<any>(
      async (dissolveMinGapArea = 0, geojson: FeatureCollection<any, any>) => {
        try {

          const dissolve = (polygon: Polygon) => {
            const noOfHoles = polygon.getNumInteriorRing();
            if (noOfHoles > 0)
            {
              const geometryFactory = new GeometryFactory()
              const holesToKeep = Array<LinearRing>()
              let noOfSmallHoles = 0;
              for (let i = 0; i < noOfHoles; i++) {
                const innerRing = polygon.getInteriorRingN(i) as LinearRing
                const innerPoly = geometryFactory.createPolygon(innerRing);
                const area = innerPoly.getArea();
                if (area > dissolveMinGapArea){
                  holesToKeep.push(innerRing);
                }
                else{
                  noOfSmallHoles++                  
                }                
              }
              if (noOfSmallHoles > 0){
                const dissolvedPolygon = holesToKeep.length > 0 ? geometryFactory.createPolygon(polygon.getExteriorRing(), holesToKeep) : geometryFactory.createPolygon(polygon.getExteriorRing());              
                return geojsonWriter.write(dissolvedPolygon)
              }
              else{
                return geojsonWriter.write(polygon)
              }               
            }
            return null;  
          }
          const featuresToDissolve = geojson.features
          const geometryFactory = new GeometryFactory();              
          const geojsonRader = new GeoJSONReader(geometryFactory);  
          const geojsonWriter = new GeoJSONWriter();  
          const processedFeatures = Array<Feature<any>>()
          featuresToDissolve.forEach((feature) => { 
            const jstsGeometry = geojsonRader.read(feature.geometry) as Polygon; 
            const polygon = dissolve(jstsGeometry)
            if (polygon){             
              const newFeature = {...feature, geometry: polygon, properties: {...feature.properties, [EDIT_MODE]: EDIT_MODE_MODIFIED} }
                processedFeatures.push(newFeature)
            }        
          }); 
      
          if (processedFeatures.length > 0){
            const result = {
              ...initialFeatureCollection,
              features: processedFeatures        
            } 
            setDrawVectorLayerGeojson(result);
            setOperationDone(result, false);
          }
          
        } catch (error) {
          console.log(error);
        }
      },

      1000,
      { maxWait: 1000 },
    );
    const value = event.target.value === '' ? 0 : Number(event.target.value);
    setMinGapArea(value);
    dissolveGeometry(value, initialFeatureCollection);
  };

  const dissolveToggled = (open: boolean) => {
    if (open) {
      store.dispatch({
        type: 'clientOperation/SET_ACTIVE_TOOL',
        operationToolId: OPERATION_TOOL_IDS.DISSOLVE,
      });
    } else {
      store.dispatch({ type: 'clientOperation/EXIT_ACTIVE_TOOL' });
    }
  };

  useEffect(
    () => {
      if (isOpen) {
        setInitialFeatureCollection(getCurrentlyDrawnGeojson());
        store.dispatch({ type: EMapToolActionType.DISABLE_ALL_DRAWING_TOOLS });
      } else {
        setInitialFeatureCollection(emptyFeatureCollection);
        setMinGapArea(0);
      }
    },
    [isOpen],
  );

  useEffect(
    () => {
      if (polygonDrawingEnabled || polylineDrawingEnabled || pointDrawingEnabled) {
        setInitialFeatureCollection(emptyFeatureCollection);
        setMinGapArea(0);
      }
    },
    [polygonDrawingEnabled, polylineDrawingEnabled, pointDrawingEnabled],
  );

  return (
    <MmgOperationContainer
      isOpen={isOpen}
      onContainerToggled={dissolveToggled}
      operationName={t('UI_DISSOLVEOPERATION')}
      disabled={disabled}
    >
      <div css={OPERATION_DESCRIPTION_STYLES}>
        {t('OP_DISSOLVEOPERATION_UI_DESCRIPTION')}
      </div>

      <div css={OPERATION_INPUT_STYLES}>
        <TextField
          label={t('PARAM_MINGAPAREA')}
          value={minGapArea}
          margin="dense"
          fullWidth
          onChange={handleInputChange}
          slotProps={{input:{ 
            endAdornment: <MmgInputUnitAdornment unitString={unit + '²'} />,         
            inputProps: { step: isLatLong ? 0.001 : 1 },
            type: 'number',
          }}}
        />
      </div>
    </MmgOperationContainer>
  );
}
